import axiosInstance from '../../../helpers/api';

export interface SmartCMAConfig {
  maxRetries: number;
  retryDelay: number;
  timeoutMs: number;
  debugMode: boolean;
}

export interface CMAAnalysisResult {
  success: boolean;
  data?: any;
  error?: string;
  retryable?: boolean;
  reportId?: string;
}

export interface DatabaseSaveResult {
  success: boolean;
  reportId?: string;
  error?: string;
  suggestions?: string[];
}

export class SmartCMAHelper {
  private config: SmartCMAConfig;

  constructor(config: Partial<SmartCMAConfig> = {}) {
    this.config = {
      maxRetries: 3,
      retryDelay: 2000,
      timeoutMs: 30000,
      debugMode: true,
      ...config
    };
  }

  private log(message: string, data?: any) {
    if (this.config.debugMode) {
      console.log(`🔧 [SmartCMA Helper] ${message}`, data || '');
    }
  }

  private async delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Enhanced CMA analysis with retry logic and better error handling
   */
  async runEnhancedCMA(propertyData: any): Promise<CMAAnalysisResult> {
    let lastError: any = null;
    
    for (let attempt = 1; attempt <= this.config.maxRetries; attempt++) {
      try {
        this.log(`Starting CMA analysis attempt ${attempt}/${this.config.maxRetries}`);
        
        // Validate input data
        const validationResult = this.validatePropertyData(propertyData);
        if (!validationResult.valid) {
          return {
            success: false,
            error: `Invalid property data: ${validationResult.errors.join(', ')}`,
            retryable: false
          };
        }

        // Run the CMA analysis
        const response = await axiosInstance.post('/api/enhanced-cma', {
          ...propertyData,
          analysis_type: 'comprehensive',
          include_ml_prediction: true,
          save_to_database: true,
          timeout_ms: this.config.timeoutMs
        }, {
          timeout: this.config.timeoutMs
        });

        if (response.data.success) {
          this.log('CMA analysis completed successfully', {
            reportId: response.data.reportId,
            comparablesCount: response.data.comparables?.length || 0,
            predictionPrice: response.data.price_prediction?.predicted_price
          });

          return {
            success: true,
            data: response.data,
            reportId: response.data.reportId
          };
        } else {
          throw new Error(response.data.message || 'CMA analysis failed');
        }

      } catch (error: any) {
        lastError = error;
        this.log(`CMA analysis attempt ${attempt} failed:`, error.message);

        // Check if this is a retryable error
        const isRetryable = this.isRetryableError(error);
        
        if (!isRetryable || attempt === this.config.maxRetries) {
          return {
            success: false,
            error: error.message || 'CMA analysis failed',
            retryable: isRetryable
          };
        }

        // Wait before retrying
        await this.delay(this.config.retryDelay * attempt);
      }
    }

    return {
      success: false,
      error: lastError?.message || 'CMA analysis failed after maximum retries',
      retryable: false
    };
  }

  /**
   * Enhanced database save with better error handling
   */
  async saveCMAToDatabase(cmaData: any, reportId?: string): Promise<DatabaseSaveResult> {
    try {
      this.log('Attempting to save CMA to database', { reportId, hasData: !!cmaData });

      // Clean and validate the data before saving
      const cleanData = this.cleanCMADataForDatabase(cmaData);
      
      const payload = {
        ...cleanData,
        reportId: reportId || cleanData.reportId,
        force_save: true, // Add flag to handle database constraint issues
        include_debug_info: this.config.debugMode
      };

      const response = await axiosInstance.post('/api/reports/save-enhanced-cma', payload);

      if (response.data.success) {
        this.log('CMA saved to database successfully', {
          reportId: response.data.reportId,
          recordsCreated: response.data.recordsCreated
        });

        return {
          success: true,
          reportId: response.data.reportId
        };
      } else {
        throw new Error(response.data.message || 'Database save failed');
      }

    } catch (error: any) {
      this.log('Database save failed:', error.message);

      // Provide specific suggestions based on the error
      const suggestions = this.getDatabaseErrorSuggestions(error);

      return {
        success: false,
        error: error.message || 'Failed to save to database',
        suggestions
      };
    }
  }

  /**
   * Validate property data before analysis
   */
  private validatePropertyData(data: any): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!data.property_address || data.property_address.trim() === '') {
      errors.push('Property address is required');
    }

    if (!data.city && !data.zipcode) {
      errors.push('City or zip code is required');
    }

    if (data.bedrooms && (data.bedrooms < 0 || data.bedrooms > 20)) {
      errors.push('Bedrooms must be between 0 and 20');
    }

    if (data.bathrooms && (data.bathrooms < 0 || data.bathrooms > 20)) {
      errors.push('Bathrooms must be between 0 and 20');
    }

    if (data.square_feet && (data.square_feet < 100 || data.square_feet > 50000)) {
      errors.push('Square feet must be between 100 and 50,000');
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }

  /**
   * Check if an error is retryable
   */
  private isRetryableError(error: any): boolean {
    if (!error) return false;

    const message = error.message?.toLowerCase() || '';
    const statusCode = error.response?.status;

    // Network errors are usually retryable
    if (error.code === 'ECONNABORTED' || error.code === 'NETWORK_ERROR') {
      return true;
    }

    // Temporary server errors are retryable
    if (statusCode >= 500 && statusCode < 600) {
      return true;
    }

    // Rate limiting is retryable
    if (statusCode === 429) {
      return true;
    }

    // Some specific error messages that are retryable
    const retryableMessages = [
      'timeout',
      'connection reset',
      'temporary failure',
      'service unavailable',
      'database busy'
    ];

    return retryableMessages.some(msg => message.includes(msg));
  }

  /**
   * Clean CMA data for database storage
   */
  private cleanCMADataForDatabase(data: any): any {
    // Remove any null or undefined values that might cause database issues
    const cleanData = JSON.parse(JSON.stringify(data, (key, value) => {
      if (value === null || value === undefined) {
        return undefined; // This will exclude the property
      }
      return value;
    }));

    // Ensure required fields exist with defaults
    return {
      success: true,
      generated_at: new Date().toISOString(),
      processing_time_ms: 0,
      ...cleanData,
      // Ensure nested objects have required structure
      subject_property: cleanData.subject_property || {},
      comparable_properties: cleanData.comparable_properties || { comparables: [] },
      price_prediction: cleanData.price_prediction || { predicted_price: 0, confidence: 0 },
      system_info: cleanData.system_info || { version: '2.2' }
    };
  }

  /**
   * Get suggestions for database errors
   */
  private getDatabaseErrorSuggestions(error: any): string[] {
    const message = error.message?.toLowerCase() || '';
    const suggestions: string[] = [];

    if (message.includes('required to return data')) {
      suggestions.push('Try refreshing the page and starting a new analysis');
      suggestions.push('Check if all required property fields are filled');
      suggestions.push('Contact support if the issue persists');
    }

    if (message.includes('constraint')) {
      suggestions.push('This property may already have a recent CMA report');
      suggestions.push('Try using a different property address');
    }

    if (message.includes('timeout')) {
      suggestions.push('The analysis is taking longer than expected');
      suggestions.push('Try again in a few minutes');
    }

    if (suggestions.length === 0) {
      suggestions.push('Try running the analysis again');
      suggestions.push('Check your internet connection');
      suggestions.push('Contact support if the problem continues');
    }

    return suggestions;
  }

  /**
   * Recover from workflow errors
   */
  async recoverWorkflow(sessionData: any): Promise<{ success: boolean; nextStep?: number; data?: any }> {
    try {
      this.log('Attempting workflow recovery', sessionData);

      // Check if we have a report ID in session storage
      const reportId = sessionStorage.getItem('currentReportId');
      if (reportId) {
        // Try to fetch the existing report
        const response = await axiosInstance.get(`/api/reports/${reportId}`);
        if (response.data.success) {
          this.log('Recovered existing report', { reportId });
          return {
            success: true,
            nextStep: 4, // Go to dashboard
            data: response.data
          };
        }
      }

      // Check workflow state to determine next step
      if (sessionData.propertyInput && sessionData.mlsData) {
        return { success: true, nextStep: 3 }; // Go to AI analysis
      } else if (sessionData.propertyInput) {
        return { success: true, nextStep: 2 }; // Go to comparable analysis
      } else {
        return { success: true, nextStep: 1 }; // Start over
      }

    } catch (error) {
      this.log('Workflow recovery failed:', error);
      return { success: false };
    }
  }
}

// Export a default instance
export const smartCMAHelper = new SmartCMAHelper(); 